'use strict'

import {
  checkMaxApp,
  getSessionStorageDF,
  h5Platform,
  removeSessionStorageDF,
  u_getDomain,
  checkUnifyShop,
  u_getUnion,
  BFD_checkApp,
  BFD_LoginOut,
} from '@/utils'
import { fecthApi } from './fetchApi'
import { getCatchAddress, setSessionStorageDF } from 'dflocationsdk'

const dfSessionKey = 'df-mall-session'

const requestDomain = u_getDomain()
export class MallApi {
  /** 接口请求底层api类型 */
  apiType: ApiType
  requestApi: MallApiModel
  constructor(type: ApiType = ApiType.FETCH) {
    this.apiType = type
    this.requestApi = RequestType(type)
  }

  /**
   * 数据请求 GET
   * @param url 请求地址
   * @param data 请求入参
   * @param requestType 请求类型
   * @returns Promise<MallResponse<T>>
   */
  Get = <T>(
    url: string,
    data?: any | null,
    requestConfig?: RequestConfig
  ): Promise<MallResponse<T>> => {
    const _config: PrivateRequestConfig = this.createRequestConfig(
      url,
      data,
      requestConfig
    )
    let returnPromise: Promise<MallResponse<T>> | null = null
    //#region 检查是否有持久化数据
    if (requestConfig?.cacheData) {
      const objStr = getSessionStorageDF(url)
      if (objStr) {
        const obj: MallResponse<T> = JSON.parse(objStr)
        returnPromise = new Promise<MallResponse<T>>((resolve, reject) => {
          resolve(obj)
        })
      }
    }
    //#endregion
    if (!returnPromise) {
      const apiPromise: Promise<MallResponse<T>> = this.requestApi.Get(
        _config.url,
        _config.data,
        requestConfig?.cacheData,
        requestConfig?.customCache,
        _config?.headers
      )
      returnPromise = apiPromise
    }
    return returnPromise
  }

  /**
   * 数据请求 POST
   * @param url 请求地址
   * @param data 请求入参
   * @param requestType 请求类型
   * @returns Promise<MallResponse<T>>
   */
  Post = <T>(
    url: string,
    data?: any | null,
    requestConfig?: RequestConfig
  ): Promise<MallResponse<T>> => {
    const _config: PrivateRequestConfig = this.createRequestConfig(
      url,
      data,
      requestConfig
    )
    let returnPromise: Promise<MallResponse<T>> | null = null
    //#region 检查是否有持久化数据
    if (requestConfig?.cacheData) {
      const objStr: any = getSessionStorageDF(url)
      if (objStr) {
        const obj: MallResponse<T> | any = JSON.parse(objStr)
        // 如果本次请求入参与上一次请求入参一致,直接拦截
        if (obj.params === JSON.stringify(_config.data)) {
          returnPromise = new Promise<MallResponse<T>>((resolve, reject) => {
            resolve(obj)
          })
          // 单独执行查询更新缓存
          this.requestApi.Post(
            _config.url,
            _config.data,
            requestConfig?.cacheData,
            requestConfig?.customCache,
            _config?.headers
          )
        }
      }
    } else {
      removeSessionStorageDF(url)
    }
    //#endregion
    if (!returnPromise) {
      const apiPromise: Promise<MallResponse<T>> = this.requestApi.Post(
        _config.url,
        _config.data,
        requestConfig?.cacheData,
        requestConfig?.customCache,
        _config?.headers
      )
      returnPromise = apiPromise
    }
    return returnPromise
  }

  /** 自定义配置处理 */
  createRequestConfig(
    url: string,
    data?: any | null,
    requestConfig?: RequestConfig
  ): PrivateRequestConfig {
    //#region 处理城市
    const address: any = getCatchAddress()
    let city = '145'
    if (address) {
      try {
        const add = JSON.parse(address)
        city = add.cityId
      } catch (ex) {
        console.log('createRequestConfig - 城市获取异常', ex)
      }
    }
    //#endregion

    //#region 处理工会属性
    const union = u_getUnion()
    const unifyShop = checkUnifyShop()
    let unionParams: any = {}
    if (union) {
      unionParams = {
        unionFlag: 1,
        union,
      }
    }
    // 统一商城不传递union
    if (unifyShop && unionParams?.union) {
      delete unionParams.union
    }
    //#endregion

    //#region 处理请求入参
    const params = {
      ...data,
      cityId: city,
      city,
      ignore: false,
      /** 平台 0.pc 1.h5 2.小程序 */
      platform: 1,
      subPlatform: h5Platform(),
      ...unionParams,
    }
    if (!union) {
      delete params.union
    }
    //#endregion

    //#region headers 处理
    const _window: any = window
    const headers: any = { ..._window?.__df_channel_sdk?.getRequestHeader() }
    if (checkMaxApp()) {
      headers.terminal = 'maxapp'
    }
    //#endregion

    const env = process.env.NODE_ENV
    let _url: string = url
    if (url.indexOf('http') <= -1 && env !== 'development') {
      _url = requestDomain + url
    }
    const _config: PrivateRequestConfig = {
      ...requestConfig,
      url: _url,
      data: params,
      headers: headers,
    }
    return _config
  }
}

/**
 * 接口返回报文整合转换
 * @param res 接口返回内容
 * @param requestUrl 接口地址
 * @param params 请求入参
 * @returns MallResponse<T>
 */
export const ResponseConvert = <T>(
  res: any,
  requestUrl: string,
  cacheData: boolean = false,
  params?: any,
  customCache?: (data: any, oldData: any, params: any) => any
): MallResponse<T> => {
  let _url = requestUrl.replace(requestDomain, '')
  if (requestUrl.indexOf('?') > -1) {
    _url = _url.slice(0, requestUrl.indexOf('?'))
  }
  const _obj: MallResponse<T> = {
    Status: -1,
    Data: null,
    Message: '',
  }
  switch (_url) {
    case '/mallapi/location/citylist':
    case '/mallapi/user/checkLogin':
    case '/mallapi/union/getunionconfig':
    case '/gw/app/search/searchContract/searchHead/v3':
    case '/gw/app/search/searchContract/searchResult/v3':
      _obj.Status = res.respCode === '00000000' ? 0 : -1
      _obj.Message = res.respMsg
      delete res.respCode
      delete res.respMsg
      _obj.Data = res
      break
    default:
      if (res?.type?.indexOf('image') > -1) {
        _obj.Data = res
        _obj.Status = 0
        _obj.Message = ''
      } else {
        _obj.Status = +res.code as any
        _obj.Message = res.msg || res.message
        _obj.Data = res?.body || res?.data
      }
      break
  }
  if (cacheData && _obj.Status === 0) {
    try {
      let newCache: any = _obj
      const oldCache = getSessionStorageDF(requestUrl)
      if (customCache) {
        newCache = customCache?.(_obj, oldCache, params)
      }
      if (newCache === null || newCache === undefined) {
        // 如果结果未null或者undefined清除缓存
        removeSessionStorageDF(requestUrl)
      } else {
        newCache.params = JSON.stringify(params)
        setSessionStorageDF(requestUrl, JSON.stringify(newCache))
      }
    } catch (ex) {
      console.log('error -------- 持久化失败', ex)
    }
  }
  if (_obj.Status === 1000000 || _obj.Status === 1000001) {
    const _loginurl: any = getSessionStorageDF('loginInfoUrl')
    const _win: any = window
    _win.dfShowGuestModal(
      (type: any) => {
        // 对应处理逻辑
        if (type === 'cancel') {
          window.history.back()
        }
      },
      _loginurl,
      res.code
    )
  } else if (_obj.Status === 101002) {
    const isBFDAPP = BFD_checkApp()
    try {
      const _win: any = window
      if (_win.__wxjs_environment === 'miniprogram') {
        // 跳转小程序
        return _win.wx.miniProgram.redirectTo({
          url: '/pages/login/login',
        })
      } else if (isBFDAPP) {
        console.log('--> 即将调百福得App登录页面')
        BFD_LoginOut()
      } else {
        const _loginurl: any = getSessionStorageDF('loginInfoUrl')

        if (_loginurl) {
          window.df_sdk_common.location.href = _loginurl
        }
      }
    } catch (e) {}
  }
  return _obj
}

/** 自定义请求配置 */
export interface RequestConfig {
  /** 请求类型 */
  requestType?: 'JSON' | 'FORMDATA'
  /** 加密请求(适用用户接口) */
  encryption?: boolean
  /** 缓存接口请求, 一旦开启如果入参未发生变化将不会再请求接口 */
  cacheData?: boolean
  /** 自定义缓存规则,不定义走简单缓存 */
  customCache?: (data: any, oldData: any, params: any) => any
  /** 请求headers */
  headers?: any | null
}

/** 外部自定义请求配置组装后结果 */
interface PrivateRequestConfig extends RequestConfig {
  /** 请求地址 */
  url: string
  /** 请求入参 */
  data?: any | null
  headers?: any | null
}

/** 接口请求底层api类型 */
export enum ApiType {
  'FETCH' = 'FETCH',
  'AXIOS' = 'AXIOS',
  'XMLHTTPREQUEST' = 'XMLHTTPREQUEST',
}

/** 获取底层请求方法 */
const RequestType = (apiType: ApiType): MallApiModel => {
  switch (apiType) {
    case ApiType.FETCH:
      return fecthApi
    default:
      return fecthApi
  }
}

/** MallApi 提供能力 */
export interface MallApiModel {
  /**
   * GET请求
   * @param url 请求地址
   * @param data 请求入参
   * @param catchData 是否对请求进行缓存, session级别
   * @param customCache 是否自定义缓存规则, 如果返回结果未null或者undefined 将清空缓存
   * @param requestType 请求类型Content-type
   * @returns Promis() > MallResponse<T>
   */
  Get: <T>(
    url: string,
    data?: any | null,
    catchData?: boolean,
    customCache?: (data: any, oldData: any, params: any) => any,
    headers?: any,
    requestType?: 'JSON' | 'FORMDATA'
  ) => Promise<MallResponse<T>>
  /**
   * POST请求
   * @param url 请求地址
   * @param data 请求入参
   * @param catchData 是否对请求进行缓存, session级别
   * @param customCache 是否自定义缓存规则, 如果返回结果未null或者undefined 将清空缓存
   * @param requestType 请求类型Content-type
   * @returns Promis() > MallResponse<T>
   */
  Post: <T>(
    url: string,
    data?: any | null,
    catchData?: boolean,
    customCache?: (data: any, oldData: any, params: any) => any,
    headers?: any,
    requestType?: 'JSON' | 'FORMDATA'
  ) => Promise<MallResponse<T>>
}

/** 接口返回数据报文体 */
export interface MallResponse<T> {
  /** 请求状态 0正常 -1异常 */
  Status: 0 | -1 | any
  /** 返回报文 */
  Data?: T | null
  /** 提示信息 */
  Message?: string
}
